import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTaskRowCounts } from '../hooks/useTaskRowCounts'
import type { ReactNode } from 'react'
import type { TaskRowCountsData } from '../hooks/useTaskRowCounts'

type Shared = { data: TaskRowCountsData | null; register: () => () => void }

const TaskRowCountsContext = createContext<Shared | null>(null)

/**
 * Computes the list rows' checklist and sub-task counts (design 28b) once for
 * the whole app, and only while at least one task list is mounted. A page with
 * one list per status group shares a single set of subscriptions.
 */
export function TaskRowCountsProvider({ children }: { children: ReactNode }) {
  const [listCount, setListCount] = useState(0)
  const register = useCallback(() => {
    setListCount((n) => n + 1)
    return () => setListCount((n) => n - 1)
  }, [])
  const data = useTaskRowCounts({ enabled: listCount > 0 })
  const value = useMemo(() => ({ data: listCount > 0 ? data : null, register }), [data, listCount, register])
  return <TaskRowCountsContext.Provider value={value}>{children}</TaskRowCountsContext.Provider>
}

const EMPTY: TaskRowCountsData = { countsByTask: new Map(), subTasksByParent: new Map() }

/** Row counts for a task list: the shared set when a provider is above, else its own. */
export function useListTaskRowCounts(): TaskRowCountsData {
  const shared = useContext(TaskRowCountsContext)
  const register = shared?.register
  useEffect(() => register?.(), [register])
  const own = useTaskRowCounts({ enabled: !shared })
  return shared ? (shared.data ?? EMPTY) : own
}
